import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { MaskedRevealLines } from "@/components/MaskedReveal";
import DashboardMockup from "@/components/DashboardMockup";
import { APP_URL } from "@/lib/urls";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const wrapRef = useRef(null);
  const mockRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, -3]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // 3D tilt on mouse move
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sy = useSpring(ry, { stiffness: 120, damping: 18 });

  useEffect(() => {
    const el = mockRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      rx.set(-py * 8);
      ry.set(px * 10);
    };
    const onLeave = () => {
      rx.set(0);
      ry.set(0);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [rx, ry]);

  return (
    <section
      ref={wrapRef}
      data-testid="hero-section"
      className="relative pt-24 lg:pt-28 pb-16 lg:pb-20 overflow-hidden"
    >
      {/* Ambient background */}
      <motion.div
        aria-hidden
        style={{ scale: scaleBg }}
        className="absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 dot-grid opacity-70" />
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 h-[900px] w-[900px] rounded-full bg-brand-radial blur-3xl opacity-70 dark:opacity-90" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Left column: 7/12 */}
        <div className="col-span-12 lg:col-span-7">
          <MaskedRevealLines
            as="h1"
            testId="hero-headline"
            ariaLabel="Every dental case, in one place."
            lines={[
              "Every dental case,",
              <>in <em className="not-italic text-brand-gradient">one place</em>.</>,
            ]}
            className="font-heading font-semibold text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.02] tracking-[-0.035em]"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Rxn3D helps dental labs and offices manage cases from Rx to delivery,
            all in one place.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href={APP_URL}
              data-testid="hero-cta-create-account"
              className="group inline-flex items-center gap-2 pl-6 pr-4 py-3.5 rounded-full bg-foreground text-background font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Create an account
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-gradient text-white transition-transform group-hover:translate-x-0.5">
                <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </a>
            <a
              href={APP_URL}
              data-testid="hero-cta-login"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full hairline glass font-semibold text-sm hover:bg-secondary transition-colors"
            >
              Login
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-md"
          >
            {[
              { k: "1,200+", v: "cases / month" },
              { k: "27%", v: "faster turnaround" },
              { k: "99.98%", v: "uptime" },
            ].map((s) => (
              <div key={s.k} data-testid={`hero-stat-${s.k}`}>
                <div className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                  {s.k}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right column: dashboard mockup */}
        <motion.div
          style={{ y, rotate: rot }}
          className="col-span-12 lg:col-span-5 relative"
        >
          <motion.div
            ref={mockRef}
            style={{ perspective: 1200, rotateX: sx, rotateY: sy, transformStyle: "preserve-3d" }}
            className="relative rounded-3xl glass-strong hairline glow-brand-strong grain overflow-hidden"
          >
            <div className="p-3 sm:p-4">
              <DashboardMockup />
            </div>

            {/* Floating card overlays */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="absolute -left-6 sm:-left-10 top-24 rounded-2xl glass-strong hairline p-4 shadow-xl"
              data-testid="hero-float-card-1"
            >
              <div className="text-xs text-muted-foreground">Prescription #RX-2841</div>
              <div className="font-heading font-semibold text-sm mt-0.5">Zirconia Crown · #14</div>
              <div className="mt-2 flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
                <span className="text-[11px] text-muted-foreground">In production</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
              className="absolute -right-4 sm:-right-8 bottom-16 rounded-2xl glass-strong hairline p-4 shadow-xl"
              data-testid="hero-float-card-2"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-brand-plum" />
                <span className="text-[11px] font-medium tracking-tight">Auto-QC passed</span>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">Ready for dispatch</div>
              <div className="mt-2 h-1 w-32 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "82%" }}
                  transition={{ duration: 1.4, delay: 1.6, ease: EASE }}
                  className="h-full bg-brand-gradient"
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
